import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Tab, 
  Tabs,
  Alert,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiCardContent-root': {
    padding: theme.spacing(3)
  }
}));

const AdminSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  border: '2px solid #e3f2fd'
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`turf-admin-tabpanel-${index}`}
      aria-labelledby={`turf-admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TurfAdminPanel = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [turfPrices, setTurfPrices] = useState([]);
  const [aerationPrices, setAerationPrices] = useState([]);
  const [grubPrices, setGrubPrices] = useState([]); // TKT-223
  const [settings, setSettings] = useState({});
  // TKT-227: overseed program bag costs + shared markup (legacy "Other
  // Suggested Healthy Turf Programs (Over Seed Programs)" admin section).
  const [overseedCosts, setOverseedCosts] = useState([]);
  const [overseedMarkup, setOverseedMarkup] = useState('');
  
  // Form states
  const [newTurfPrice, setNewTurfPrice] = useState({
    add_sq_ft_start: '',
    add_sq_ft_end: '',
    add_price_per_application: ''
  });
  const [newAerationPrice, setNewAerationPrice] = useState({
    add_sq_ft_start: '',
    add_sq_ft_end: '',
    add_price: ''
  });
  const [newGrubPrice, setNewGrubPrice] = useState({
    add_sq_ft_start: '',
    add_sq_ft_end: '',
    add_price: ''
  });

  // TKT-222: Edit (pen) + Delete (garbage) were dead icons. Unified edit
  // dialog + delete confirmation modal state, keyed by 'turf' | 'aeration'
  // so both tables reuse the same handlers.
  const [editRow, setEditRow] = useState(null); // { type, id, sq_ft_start, sq_ft_end, price }
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id, label }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (activeTab === 0) {
        // Load turf prices
        const response = await fetch('/api/admin/turf/turf-prices', { headers });
        if (!response.ok) throw new Error('Failed to load turf prices');
        const data = await response.json();
        setTurfPrices(data.rows || []);
      } else if (activeTab === 1) {
        // Load aeration prices
        const response = await fetch('/api/admin/turf/aeration-prices', { headers });
        if (!response.ok) throw new Error('Failed to load aeration prices');
        const data = await response.json();
        setAerationPrices(data.rows || []);
      } else if (activeTab === 2) {
        // TKT-223: load grub control prices
        const response = await fetch('/api/admin/turf/grub-prices', { headers });
        if (!response.ok) throw new Error('Failed to load grub control prices');
        const data = await response.json();
        setGrubPrices(data.rows || []);
      } else if (activeTab === 3) {
        // TKT-227: load overseed bag costs + the shared overseed markup
        const [costsRes, settingsRes] = await Promise.all([
          fetch('/api/admin/turf/overseed-costs', { headers }),
          fetch('/api/admin/turf/settings', { headers })
        ]);
        if (!costsRes.ok) throw new Error('Failed to load overseed program costs');
        if (!settingsRes.ok) throw new Error('Failed to load settings');
        const costsData = await costsRes.json();
        const settingsData = await settingsRes.json();
        setOverseedCosts(costsData.costs || []);
        setOverseedMarkup(settingsData.overseed_price?.value ?? '');
      } else if (activeTab === 4) {
        // Load settings
        const response = await fetch('/api/admin/turf/settings', { headers });
        if (!response.ok) throw new Error('Failed to load settings');
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
  };

  const addTurfPrice = async () => {
    if (!newTurfPrice.add_sq_ft_start || !newTurfPrice.add_sq_ft_end || !newTurfPrice.add_price_per_application) {
      setError('All fields are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/turf/turf-prices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTurfPrice)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add turf price');
      }

      setSuccess('Turf price added successfully');
      setNewTurfPrice({ add_sq_ft_start: '', add_sq_ft_end: '', add_price_per_application: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const addAerationPrice = async () => {
    if (!newAerationPrice.add_sq_ft_start || !newAerationPrice.add_sq_ft_end || !newAerationPrice.add_price) {
      setError('All fields are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/turf/aeration-prices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAerationPrice)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add aeration price');
      }

      setSuccess('Aeration price added successfully');
      setNewAerationPrice({ add_sq_ft_start: '', add_sq_ft_end: '', add_price: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // TKT-223: add a grub control pricing tier (mirrors aeration).
  const addGrubPrice = async () => {
    if (!newGrubPrice.add_sq_ft_start || !newGrubPrice.add_sq_ft_end || !newGrubPrice.add_price) {
      setError('All fields are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/turf/grub-prices', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newGrubPrice)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add grub price');
      }
      setSuccess('Grub control price added successfully');
      setNewGrubPrice({ add_sq_ft_start: '', add_sq_ft_end: '', add_price: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateSetting = async (settingName, value) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/turf/settings/update', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: settingName, value })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update setting');
      }

      setSuccess('Setting updated successfully');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // TKT-227: persist one overseed bag cost (save on blur, like markups).
  const updateOverseedCost = async (alias, value) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/turf/overseed-costs/update', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias, value })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update overseed cost');
      }
      setSuccess(t('turfAdmin.overseed.costSaved', 'Overseed cost updated successfully'));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // TKT-222: open the edit dialog for a row. The grid sends the range as
  // a "start - end" string in cell[1] and the base price in cell[2], so
  // parse those back into editable fields.
  const openEdit = (type, row) => {
    const rangeStr = String(row.cell[1] || '');
    const [startStr, endStr] = rangeStr.split(' - ');
    setEditRow({
      type,
      id: row.cell[0],
      sq_ft_start: (startStr || '').trim(),
      sq_ft_end: (endStr || '').trim(),
      price: row.cell[2]
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setError('');
    try {
      setEditSaving(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const isTurf = editRow.type === 'turf';
      const endpoint = { turf: 'turf-prices', aeration: 'aeration-prices', grub: 'grub-prices' }[editRow.type];
      const url = `/api/admin/turf/${endpoint}/${editRow.id}`;
      // Turf stores the value as application_price; aeration + grub use price.
      const body = isTurf
        ? { sq_ft_start: editRow.sq_ft_start, sq_ft_end: editRow.sq_ft_end, application_price: editRow.price }
        : { sq_ft_start: editRow.sq_ft_start, sq_ft_end: editRow.sq_ft_end, price: editRow.price };
      const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update price');
      }
      setSuccess('Pricing tier updated successfully');
      setEditRow(null);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const endpoint = { turf: 'turf-prices', aeration: 'aeration-prices', grub: 'grub-prices' }[deleteTarget.type];
      const url = `/api/admin/turf/${endpoint}/${deleteTarget.id}`;
      const response = await fetch(url, { method: 'DELETE', headers });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete price');
      }
      setSuccess('Pricing tier deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#2c5aa0', fontWeight: 'bold' }}>
        🌱 Turf Program Administration
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        Manage pricing tiers and markup percentages for turf programs. Changes will automatically apply to new quotes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="turf admin tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Turf Program Prices" />
          <Tab label="Aeration Prices" />
          <Tab label="Grub Control Prices" />
          <Tab label={t('turfAdmin.overseed.tab', 'Overseed Programs')} />
          <Tab label="Markup Settings" />
        </Tabs>
      </Box>

      {/* Turf Program Prices Tab */}
      <TabPanel value={activeTab} index={0}>
        <AdminSection>
          <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32' }}>
            🌿 Suggested Healthy Turf Programs
          </Typography>
          
          {/* Add New Turf Price Form */}
          <Card sx={{ mb: 3, bgcolor: '#e8f5e8' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Add New Pricing Tier
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT Start"
                    type="number"
                    value={newTurfPrice.add_sq_ft_start}
                    onChange={(e) => setNewTurfPrice({...newTurfPrice, add_sq_ft_start: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT End"
                    type="number"
                    value={newTurfPrice.add_sq_ft_end}
                    onChange={(e) => setNewTurfPrice({...newTurfPrice, add_sq_ft_end: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Price Per Application"
                    type="number"
                    step="0.01"
                    value={newTurfPrice.add_price_per_application}
                    onChange={(e) => setNewTurfPrice({...newTurfPrice, add_price_per_application: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={addTurfPrice}
                    disabled={loading}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Turf Prices Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#2e7d32' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SQ FT Range</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Base Price</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Simple (3)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Hybrid (5)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Premium (6)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ultra (6)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {turfPrices.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip label={row.cell[1]} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatCurrency(row.cell[2])}</TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[3])}
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[4])}
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[5])}
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[6])}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEdit('turf', row)}
                        aria-label={`Edit turf pricing tier ${row.cell[1]}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget({ type: 'turf', id: row.cell[0], label: row.cell[1] })}
                        aria-label={`Delete turf pricing tier ${row.cell[1]}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AdminSection>
      </TabPanel>

      {/* Aeration Prices Tab */}
      <TabPanel value={activeTab} index={1}>
        <AdminSection>
          <Typography variant="h6" sx={{ mb: 3, color: '#1976d2' }}>
            💨 Aeration Program Pricing
          </Typography>
          
          {/* Add New Aeration Price Form */}
          <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Add New Aeration Pricing Tier
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT Start"
                    type="number"
                    value={newAerationPrice.add_sq_ft_start}
                    onChange={(e) => setNewAerationPrice({...newAerationPrice, add_sq_ft_start: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT End"
                    type="number"
                    value={newAerationPrice.add_sq_ft_end}
                    onChange={(e) => setNewAerationPrice({...newAerationPrice, add_sq_ft_end: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Base Price"
                    type="number"
                    step="0.01"
                    value={newAerationPrice.add_price}
                    onChange={(e) => setNewAerationPrice({...newAerationPrice, add_price: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={addAerationPrice}
                    disabled={loading}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Aeration Prices Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SQ FT Range</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Base Price</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Final Price (w/ Markup)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aerationPrices.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip label={row.cell[1]} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatCurrency(row.cell[2])}</TableCell>
                    <TableCell sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[3])}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEdit('aeration', row)}
                        aria-label={`Edit aeration pricing tier ${row.cell[1]}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget({ type: 'aeration', id: row.cell[0], label: row.cell[1] })}
                        aria-label={`Delete aeration pricing tier ${row.cell[1]}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AdminSection>
      </TabPanel>

      {/* Grub Control Prices Tab (TKT-223) */}
      <TabPanel value={activeTab} index={2}>
        <AdminSection>
          <Typography variant="h6" sx={{ mb: 3, color: '#6d4c41' }}>
            🐛 Grub Control Program Pricing
          </Typography>

          {/* Add New Grub Price Form */}
          <Card sx={{ mb: 3, bgcolor: '#efebe9' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Add New Grub Control Pricing Tier
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT Start"
                    type="number"
                    value={newGrubPrice.add_sq_ft_start}
                    onChange={(e) => setNewGrubPrice({ ...newGrubPrice, add_sq_ft_start: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="SQ FT End"
                    type="number"
                    value={newGrubPrice.add_sq_ft_end}
                    onChange={(e) => setNewGrubPrice({ ...newGrubPrice, add_sq_ft_end: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Base Price"
                    type="number"
                    step="0.01"
                    value={newGrubPrice.add_price}
                    onChange={(e) => setNewGrubPrice({ ...newGrubPrice, add_price: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addGrubPrice}
                    disabled={loading}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Grub Prices Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#6d4c41' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SQ FT Range</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Base Price</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Final Price (w/ Markup)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grubPrices.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip label={row.cell[1]} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatCurrency(row.cell[2])}</TableCell>
                    <TableCell sx={{ color: '#6d4c41', fontWeight: 'bold' }}>
                      {formatCurrency(row.cell[3])}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEdit('grub', row)}
                        aria-label={`Edit grub control pricing tier ${row.cell[1]}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget({ type: 'grub', id: row.cell[0], label: row.cell[1] })}
                        aria-label={`Delete grub control pricing tier ${row.cell[1]}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AdminSection>
      </TabPanel>

      {/* Overseed Programs Tab (TKT-227) — mirrors the legacy admin section
          "Other Suggested Healthy Turf Programs (Over Seed Programs)" */}
      <TabPanel value={activeTab} index={3}>
        <AdminSection>
          <Typography variant="h6" sx={{ mb: 1, color: '#2e7d32' }}>
            🌾 {t('turfAdmin.overseed.title', 'Other Suggested Healthy Turf Programs (Over Seed Programs)')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, color: '#666' }}>
            {t('turfAdmin.overseed.description', 'Bag costs feed the legacy per-square-foot formulas for each overseed option on the quote forms. The markup is the default Over Seed MU % applied to new quotes (adjustable per quote).')}
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('turfAdmin.overseed.markupLabel', 'Over Seed - Mark Up %')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('turfAdmin.overseed.markupField', 'Markup %')}
                    value={overseedMarkup}
                    onChange={(e) => setOverseedMarkup(e.target.value)}
                    onBlur={(e) => updateSetting('overseed_price', e.target.value)}
                    inputProps={{ min: 0, 'aria-label': t('turfAdmin.overseed.markupLabel', 'Over Seed - Mark Up %') }}
                    InputProps={{
                      endAdornment: <Typography variant="body2" sx={{ color: '#f57c00' }}>%</Typography>
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
            {overseedCosts.map((cost) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cost.alias}>
                <Card sx={{ bgcolor: '#e8f5e8' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {cost.label}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('turfAdmin.overseed.costField', 'Bag Cost')}
                      value={cost.value ?? ''}
                      onChange={(e) => {
                        setOverseedCosts((prev) => prev.map((c) => (
                          c.alias === cost.alias ? { ...c, value: e.target.value } : c
                        )));
                      }}
                      onBlur={(e) => updateOverseedCost(cost.alias, e.target.value)}
                      inputProps={{ min: 0, step: '0.01', 'aria-label': cost.label }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ color: '#2e7d32', mr: 0.5 }}>$</Typography>
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AdminSection>
      </TabPanel>

      {/* Markup Settings Tab */}
      <TabPanel value={activeTab} index={4}>
        <AdminSection>
          <Typography variant="h6" sx={{ mb: 3, color: '#f57c00' }}>
            ⚙️ Global Markup Percentages
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: '#666' }}>
            These percentages are applied to base prices to calculate final customer pricing. 
            Changes will take effect immediately for new quotes.
          </Typography>

          <Grid container spacing={3}>
            {Object.entries(settings).map(([key, setting]) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                <Card sx={{ bgcolor: '#fff3e0' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {setting.description}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      label="Markup %"
                      value={setting.value}
                      onChange={(e) => {
                        const newSettings = { ...settings };
                        newSettings[key] = { ...newSettings[key], value: e.target.value };
                        setSettings(newSettings);
                      }}
                      onBlur={(e) => updateSetting(key, e.target.value)}
                      InputProps={{
                        endAdornment: <Typography variant="body2" sx={{ color: '#f57c00' }}>%</Typography>
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AdminSection>
      </TabPanel>

      {/* TKT-222: Edit pricing tier dialog (turf + aeration). */}
      <Dialog open={Boolean(editRow)} onClose={() => !editSaving && setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {{ turf: 'Edit Turf Pricing Tier', aeration: 'Edit Aeration Pricing Tier', grub: 'Edit Grub Control Pricing Tier' }[editRow?.type] || 'Edit Pricing Tier'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="SQ FT Start"
                type="number"
                value={editRow?.sq_ft_start ?? ''}
                onChange={(e) => setEditRow({ ...editRow, sq_ft_start: e.target.value })}
                inputProps={{ 'aria-label': 'Square footage start' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="SQ FT End"
                type="number"
                value={editRow?.sq_ft_end ?? ''}
                onChange={(e) => setEditRow({ ...editRow, sq_ft_end: e.target.value })}
                inputProps={{ 'aria-label': 'Square footage end' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={editRow?.type === 'turf' ? 'Price Per Application' : 'Base Price'}
                type="number"
                value={editRow?.price ?? ''}
                onChange={(e) => setEditRow({ ...editRow, price: e.target.value })}
                inputProps={{ 'aria-label': 'Price', step: '0.01' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} disabled={editSaving} aria-label="Cancel edit">
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveEdit} disabled={editSaving} aria-label="Save pricing tier">
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TKT-222: Delete pricing tier confirmation (no browser confirm). */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Pricing Tier</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the {{ turf: 'turf', aeration: 'aeration', grub: 'grub control' }[deleteTarget?.type] || ''} pricing tier for{' '}
            <strong>{deleteTarget?.label}</strong> sq ft? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} aria-label="Cancel delete">
            Cancel
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={confirmDelete} disabled={deleting} aria-label="Confirm delete pricing tier">
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TurfAdminPanel;
